import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const DragHandle = () => (
  <div className="tbb-quiz__ranking-handle" aria-hidden="true">
    {Array.from({ length: 6 }).map((_, i) => (
      <span key={i} className="tbb-quiz__ranking-handle-dot" />
    ))}
  </div>
);

const SortableItem = ({ id, label, rank, image }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`tbb-quiz__ranking-item${isDragging ? " tbb-quiz__ranking-item--dragging" : ""}${image?.url ? " tbb-quiz__ranking-item--with-image" : ""}`}
      {...attributes}
      {...listeners}
    >
      <div className="tbb-quiz__ranking-main">
        <span className="tbb-quiz__ranking-rank">{rank}</span>
        <span className="tbb-quiz__ranking-label">{label}</span>
        <DragHandle />
      </div>
      {image?.url && (
        <span className="tbb-quiz__ranking-media">
          <img
            className="tbb-quiz__ranking-media-img"
            src={image.url}
            alt={image.alt || ""}
            loading="lazy"
          />
        </span>
      )}
    </div>
  );
};

const RankingDragDrop = ({ question, answer, onAnswer }) => {
  const [items, setItems] = useState(() => {
    // If there's a saved answer, restore that order
    if (Array.isArray(answer) && answer.length > 0) {
      return answer.map((id) => question.items.find((it) => it.id === id)).filter(Boolean);
    }
    return question.items;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    // Report initial order on first render
    if (!answer || answer.length === 0) {
      onAnswer(items.map((it) => it.id));
    }
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((it) => it.id === active.id);
      const newIndex = prev.findIndex((it) => it.id === over.id);
      const next = arrayMove(prev, oldIndex, newIndex);
      onAnswer(next.map((it) => it.id));
      return next;
    });
  };

  const handleClear = () => {
    const reset = [...question.items];
    setItems(reset);
    onAnswer(reset.map((it) => it.id));
  };

  return (
    <div className="tbb-quiz__field">
      <p className="tbb-quiz__ranking-instruction">Drag and drop to rank options</p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((it) => it.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="tbb-quiz__ranking-list">
            {items.map((item, idx) => (
              <SortableItem
                key={item.id}
                id={item.id}
                label={item.label}
                rank={idx + 1}
                image={item.image}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <button type="button" className="tbb-quiz__ranking-clear" onClick={handleClear}>
        Clear all
      </button>
    </div>
  );
};

export default RankingDragDrop;
